import { memo, useMemo, useState } from 'react';
import { Box, ButtonBase, Typography, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import { neutralGrey } from '@/styles/theme';
import type { StatusHistoryEntry } from '@/types';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import PauseIcon from '@mui/icons-material/Pause';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

/** Map icon name (from flow config) to MUI icon component */
const ICON_COMPONENTS: Record<string, React.ElementType> = {
  Schedule: ScheduleIcon,
  ThumbUpAlt: ThumbUpAltIcon,
  DirectionsCar: DirectionsCarIcon,
  Build: BuildIcon,
  Pause: PauseIcon,
  LocalShipping: LocalShippingIcon,
  Inventory2: Inventory2Icon,
  TaskAlt: TaskAltIcon,
  ReceiptLong: ReceiptLongIcon,
  CheckCircle: CheckCircleIcon,
  Cancel: CancelIcon,
};

const FALLBACK_ICONS: Record<string, React.ElementType> = {
  pending: ScheduleIcon,
  confirmed: ThumbUpAltIcon,
  vehicle_arrived: DirectionsCarIcon,
  in_progress: BuildIcon,
  paused: PauseIcon,
  waiting_parts: LocalShippingIcon,
  parts_received: Inventory2Icon,
  work_completed: TaskAltIcon,
  ready_to_invoice: ReceiptLongIcon,
  on_consolidated_invoice: ReceiptLongIcon,
  invoiced: ReceiptLongIcon,
  completed: CheckCircleIcon,
  cancelled: CancelIcon,
};

// Future-step labels shown as a call-to-action while in a given currentStatus
// (#1023) — e.g. quote's next steps read "Tee varaus"/"Hylkää tarjous"
// instead of the destination state's name. Once actually reached, the step
// renders its normal status.* label (see history rendering below).
const FUTURE_ACTION_LABEL_KEYS: Record<string, Record<string, string>> = {
  quote: {
    pending: 'stepper.actionMakeBooking',
    quote_rejected: 'stepper.actionRejectQuote',
  },
};

const ARROW_W = 10;
const STEP_H = 36;
const PAST_ALPHA = 0.19;
const FUTURE_ALPHA = 0.07;
const GRADIENT_SHIFT = 8;

/** Parse hex to RGB tuple. */
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** Format RGB to hex string. */
function toHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`;
}

/** Blend a hex color with a background at given alpha to produce an opaque result. */
function blendColor(fgHex: string, bgHex: string, alpha: number): string {
  const [fR, fG, fB] = parseHex(fgHex);
  const [bR, bG, bB] = parseHex(bgHex);
  return toHex(fR * alpha + bR * (1 - alpha), fG * alpha + bG * (1 - alpha), fB * alpha + bB * (1 - alpha));
}

/** Shift brightness of a hex color by amount (positive = lighter, negative = darker). */
function shiftBrightness(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex(r + amount, g + amount, b + amount);
}

interface WorkOrderStatusStepperProps {
  currentStatus: string;
  history: StatusHistoryEntry[];
  onStatusChange: (targetStatus: string) => void;
  disabled?: boolean;
}

interface HistoryStatusStep {
  type: 'status';
  status: string;
}

interface HistoryEllipsisStep {
  type: 'ellipsis';
  hiddenCount: number;
}

type DisplayHistoryStep = HistoryStatusStep | HistoryEllipsisStep;

function compressAlternatingHistory(steps: string[]): DisplayHistoryStep[] {
  if (steps.length < 4) {
    return steps.map((status) => ({ type: 'status', status }));
  }

  const displaySteps: DisplayHistoryStep[] = [];
  let index = 0;

  while (index < steps.length) {
    const first = steps[index];
    const second = steps[index + 1];

    if (!first) {
      index += 1;
      continue;
    }

    if (
      second &&
      first !== second &&
      steps[index + 2] === first &&
      steps[index + 3] === second
    ) {
      let runEnd = index + 4;

      while (runEnd < steps.length && steps[runEnd] === steps[runEnd - 2]) {
        runEnd += 1;
      }

      const hiddenCount = runEnd - index - 3;

      displaySteps.push(
        { type: 'status', status: first },
        { type: 'status', status: second },
        { type: 'ellipsis', hiddenCount },
      );

      index = runEnd;
      continue;
    }

    displaySteps.push({ type: 'status', status: first });
    index += 1;
  }

  return displaySteps;
}

function WorkOrderStatusStepperInner({
  currentStatus,
  history,
  onStatusChange,
  disabled,
}: WorkOrderStatusStepperProps) {
  const { t } = useTranslation('workorders');
  const theme = useTheme();
  const { transitions, statusColors, statusIcons } = useWorkOrderFlow();
  const [isExpanded, setIsExpanded] = useState(false);

  // Build steps from history: each entry's newStatus in chronological order
  const historySteps = useMemo(() => {
    if (history.length === 0) return [currentStatus];
    const sorted = [...history].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const steps = sorted
      .filter((h) => h.oldStatus !== h.newStatus)
      .map((h) => h.newStatus);
    // Ensure current status is the last step if not already
    if (steps[steps.length - 1] !== currentStatus) {
      steps.push(currentStatus);
    }
    return steps;
  }, [history, currentStatus]);

  const displayHistorySteps = useMemo(() => {
    return isExpanded
      ? historySteps.map((status) => ({ type: 'status', status }) satisfies DisplayHistoryStep)
      : compressAlternatingHistory(historySteps);
  }, [historySteps, isExpanded]);
  const canToggleCompression = useMemo(
    () => compressAlternatingHistory(historySteps).some((step) => step.type === 'ellipsis'),
    [historySteps],
  );

  // Future steps: valid transitions from current status (clickable).
  // Invoiced and consolidated-invoice attach happen in billing, so they are
  // drawn when they are the current or a past step, not as a click (#3941).
  const futureSteps = useMemo(() => {
    const allowed = transitions[currentStatus] ?? [];
    return allowed.filter(
      (s) =>
        s !== 'cancelled' &&
        s !== 'paused' &&
        s !== 'in_progress' &&
        s !== 'invoiced' &&
        s !== 'on_consolidated_invoice',
    );
  }, [transitions, currentStatus]);

  const allowedTargets = transitions[currentStatus] ?? [];
  const paperColor = theme.palette.background.paper;
  const futureBase = blendColor(neutralGrey, paperColor, FUTURE_ALPHA);

  if (currentStatus === 'cancelled') return null;

  const totalSteps = displayHistorySteps.length + futureSteps.length;
  const currentIndex = displayHistorySteps.length - 1; // last history step is current

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        overflowX: 'auto',
        flexWrap: 'nowrap',
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 2 },
      }}
    >
      {/* History steps (past + current) */}
      {displayHistorySteps.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isPast = index < currentIndex;

        if (step.type === 'ellipsis') {
          const bgColor = blendColor(neutralGrey, paperColor, PAST_ALPHA);
          const fgColor = neutralGrey;

          return (
            <Tooltip
              key={`history-${index}-ellipsis`}
              title={isExpanded ? t('stepper.hideFullHistory') : t('stepper.showFullHistory', { count: step.hiddenCount })}
              arrow
            >
              <ButtonBase
                onClick={() => setIsExpanded((prev) => !prev)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: STEP_H,
                  minWidth: 52,
                  pl: index === 0 ? 1.5 : `${ARROW_W + 8}px`,
                  pr: `${ARROW_W + 8}px`,
                  flexShrink: 0,
                  zIndex: totalSteps - index,
                  bgcolor: bgColor,
                  color: fgColor,
                  ...(index === 0 && { borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }),
                  transition: 'filter 0.15s ease',
                  '&:hover': { filter: 'brightness(0.98)' },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: -ARROW_W,
                    top: 0,
                    width: 0,
                    height: 0,
                    borderTop: `${STEP_H / 2}px solid transparent`,
                    borderBottom: `${STEP_H / 2}px solid transparent`,
                    borderLeft: `${ARROW_W}px solid ${bgColor}`,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: fgColor,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    letterSpacing: '0.08em',
                  }}
                >
                  ...
                </Typography>
              </ButtonBase>
            </Tooltip>
          );
        }

        const color = statusColors[step.status] || neutralGrey;
        const iconName = statusIcons[step.status];
        const Icon = (iconName ? ICON_COMPONENTS[iconName] : undefined) ?? FALLBACK_ICONS[step.status] ?? ScheduleIcon;

        const bgColor = isCurrent ? color : blendColor(color, paperColor, PAST_ALPHA);
        const fgColor = isCurrent ? theme.palette.common.white : color;

        return (
          <Tooltip
            key={`history-${index}-${step.status}`}
            title={t(`status.${step.status}`)}
            arrow
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: isPast ? 0 : 0.75,
                height: STEP_H,
                pl: index === 0 ? 1.5 : `${ARROW_W + 8}px`,
                pr: `${ARROW_W + (isPast ? 6 : 8)}px`,
                flexShrink: 0,
                zIndex: totalSteps - index,
                cursor: 'default',
                bgcolor: bgColor,
                ...(index === 0 && { borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }),
                transition: 'filter 0.15s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -ARROW_W,
                  top: 0,
                  width: 0,
                  height: 0,
                  borderTop: `${STEP_H / 2}px solid transparent`,
                  borderBottom: `${STEP_H / 2}px solid transparent`,
                  borderLeft: `${ARROW_W}px solid ${bgColor}`,
                },
              }}
            >
              <Icon sx={{ fontSize: isPast ? 14 : 16, color: fgColor, flexShrink: 0 }} />
              {!isPast && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: fgColor,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  }}
                >
                  {t(`status.${step.status}`)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}

      {isExpanded && canToggleCompression && (
        <Tooltip
          title={t('stepper.hideFullHistory')}
          arrow
        >
          <ButtonBase
            onClick={() => setIsExpanded(false)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: STEP_H,
              minWidth: 64,
              pl: `${ARROW_W + 8}px`,
              pr: `${ARROW_W + 8}px`,
              flexShrink: 0,
              zIndex: futureSteps.length + 1,
              bgcolor: blendColor(neutralGrey, paperColor, PAST_ALPHA),
              color: 'text.secondary',
              transition: 'filter 0.15s ease',
              '&:hover': { filter: 'brightness(0.98)' },
              '&::after': {
                content: '""',
                position: 'absolute',
                right: -ARROW_W,
                top: 0,
                width: 0,
                height: 0,
                borderTop: `${STEP_H / 2}px solid transparent`,
                borderBottom: `${STEP_H / 2}px solid transparent`,
                borderLeft: `${ARROW_W}px solid ${blendColor(neutralGrey, paperColor, PAST_ALPHA)}`,
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                lineHeight: 1,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
              }}
            >
              {t('stepper.hide')}
            </Typography>
          </ButtonBase>
        </Tooltip>
      )}

      {/* Future steps (valid transitions) */}
      {futureSteps.map((step, index) => {
        const isClickable = !disabled && allowedTargets.includes(step);
        const iconName = statusIcons[step];
        const Icon = (iconName ? ICON_COMPONENTS[iconName] : undefined) ?? FALLBACK_ICONS[step] ?? ScheduleIcon;
        const labelKey = FUTURE_ACTION_LABEL_KEYS[currentStatus]?.[step] ?? `status.${step}`;

        const futureStart = shiftBrightness(futureBase, GRADIENT_SHIFT);
        const futureEnd = shiftBrightness(futureBase, -GRADIENT_SHIFT);

        return (
          <Tooltip
            key={`future-${step}`}
            title={isClickable ? `${t('stepper.clickToChange')} ${t(labelKey)}` : ''}
            arrow
          >
            <Box
              onClick={() => { if (isClickable) onStatusChange(step); }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                height: STEP_H,
                pl: `${ARROW_W + 8}px`,
                pr: `${ARROW_W + 8}px`,
                flexShrink: 0,
                zIndex: totalSteps - displayHistorySteps.length - index,
                cursor: isClickable ? 'pointer' : 'default',
                background: `linear-gradient(to right, ${futureStart}, ${futureEnd})`,
                transition: 'filter 0.15s ease',
                '&:hover': isClickable ? { filter: 'brightness(1.15)' } : undefined,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -ARROW_W,
                  top: 0,
                  width: 0,
                  height: 0,
                  borderTop: `${STEP_H / 2}px solid transparent`,
                  borderBottom: `${STEP_H / 2}px solid transparent`,
                  borderLeft: `${ARROW_W}px solid ${futureEnd}`,
                },
              }}
            >
              <Icon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                }}
              >
                {t(labelKey)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export const WorkOrderStatusStepper = memo(
  WorkOrderStatusStepperInner,
  (prev, next) =>
    prev.currentStatus === next.currentStatus &&
    prev.disabled === next.disabled &&
    prev.history === next.history,
);
